"use client"

import { ChevronLeft, ChevronRight } from "lucide-react"

interface PaginationStatsProps {
  currentPage: number
  totalPages: number
  totalResults: number
  resultsPerPage: number
  onPageChange: (page: number) => void
}

export function PaginationStats({
  currentPage,
  totalPages,
  totalResults,
  resultsPerPage,
  onPageChange,
}: PaginationStatsProps) {
  const startResult = (currentPage - 1) * resultsPerPage + 1
  const endResult = Math.min(currentPage * resultsPerPage, totalResults)

  const handlePrevious = () => {
    if (currentPage > 1) {
      console.log("Navigating to previous page:", currentPage - 1)
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      console.log("Navigating to next page:", currentPage + 1)
      onPageChange(currentPage + 1)
    }
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
      {/* Stats text */}
      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold text-gray-900">{startResult}</span> to{" "}
        <span className="font-semibold text-gray-900">{endResult}</span> of{" "}
        <span className="font-semibold text-gray-900">{totalResults}</span> results
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft size={20} className="text-gray-600" />
        </button>

        <div className="flex items-center gap-1">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[2.5rem] h-10 rounded-lg font-medium text-sm transition ${
                currentPage === page
                  ? "bg-indigo-600 text-white"
                  : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <ChevronRight size={20} className="text-gray-600" />
        </button>
      </div>
    </div>
  )
}
